import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import {
  fetchAjaxListData,
  mapAjaxRowsToOptions,
} from "../../utils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import EmojiPicker from "emoji-picker-react";
import {
  BsFileEarmark,
  BsPaperclip,
  BsSendFill,
  BsX,
  BsTypeBold,
  BsTypeItalic,
  BsTypeUnderline,
  BsTypeStrikethrough,
  BsListUl,
  BsListOl,
  BsQuote,
  BsCode,
  BsEmojiSmile,
  BsStars,
} from "react-icons/bs";
import "./AppSupportTicketDetails.css";
import {
  Utils,
  fetchWithAuth,
  handleSessionExpired,
  getSupportFieldsForApp,
  formatDateTime,
  isAppSupportConversationClosed,
  getAppSupportConversationStatusDisplay,
  normalizeAppSupportMessageSenderType,
  buildSupportTicketFileAcceptAttribute,
  isSupportTicketFileAllowed,
  normalizeFileName,
  resolveFileKind,
  extractFilesFromText,
  APP_SUPPORT_UPLOADS_PATH,
  SUPPORT_TICKET_ALLOWED_FILE_TYPES,
  loadSupportOpenTabs,
  touchSupportOpenTab,
  removeSupportOpenTab,
  supportOpenTabLabelParts,
  normalizeTicketListTab,
  supportTicketListTabStorageKey,
  getLocalStorage,
  parseSupportMailboxAddress,
  getAvatarInitials,
  getSupportContextScalar,
  formatPersonDisplayName,
  can,
  plainSupportTextToHtml,
} from "../../utils";

/** HTTP polling for new email / app replies (no WebSocket). */
const SUPPORT_TICKETS_POLL_MS = 8000;
const PENDING_REPLY_ID_PREFIX = "pending-reply-";

const REPLY_QUILL_MODULES = { toolbar: false };
const REPLY_QUILL_FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "blockquote",
  "code",
  "code-block",
];

const REPLY_FORMAT_STATE_INITIAL = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  listBullet: false,
  listOrdered: false,
  blockquote: false,
  codeBlock: false,
};

const REPLY_EDITOR_HEIGHT_STORAGE_KEY = "ns-support-reply-editor-height";
const REPLY_EDITOR_HEIGHT_DEFAULT = 120;
const REPLY_EDITOR_HEIGHT_MIN = 72;
const REPLY_EDITOR_HEIGHT_MAX = 420;

function readStoredReplyEditorHeight() {
  try {
    const raw = localStorage.getItem(REPLY_EDITOR_HEIGHT_STORAGE_KEY);
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n)) {
      return Math.min(REPLY_EDITOR_HEIGHT_MAX, Math.max(REPLY_EDITOR_HEIGHT_MIN, n));
    }
  } catch {
    /* ignore */
  }
  return REPLY_EDITOR_HEIGHT_DEFAULT;
}

function replyFormatBtnClass(active) {
  return `ns-support-reply-format__btn${active ? " ns-support-reply-format__btn--active" : ""}`;
}

/** Normalize field_id for lookup (spaces → underscores). */
function normalizeSupportFieldId(raw) {
  return String(raw || "").trim().replace(/\s+/g, "_");
}

function looksLikeRawSupportFieldId(value) {
  const v = normalizeSupportFieldId(value).toLowerCase();
  return /^fld_/.test(v) || /^std_/.test(v);
}

function isEmptyQuillHtml(html) {
  const stripped = String(html || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
  return !stripped;
}

function quillHtmlToPlainPreview(html) {
  return String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function looksLikeSupportHtmlMessage(text) {
  return /<(?:p|div|br|strong|em|b|i|u|s|ul|ol|li|blockquote|code|pre|span|img)\b/i.test(
    String(text || "")
  );
}

function stripEmptyDisplayParagraphs(html) {
  // Remove spacer <p> (Outlook/Word) only — never strip <p> that contains <br>.
  const emptyInner = String.raw`(?:\s|&nbsp;|&#160;|<span\b[^>]*>(?:\s|&nbsp;|&#160;)*<\/span>)*`;
  let s = String(html || "");
  let prev = "";
  while (s !== prev) {
    prev = s;
    s = s.replace(new RegExp(`<p\\b[^>]*>${emptyInner}<\\/p>`, "gi"), "");
  }
  return s;
}

function sanitizeSupportMessageHtmlForDisplay(html) {
  let s = String(html || "");
  s = s.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  s = s.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "");
  s = s.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  s = s.replace(/javascript\s*:/gi, "");
  s = s.replace(/<img\b([^>]*?)>/gi, (full, attrs) => {
    const srcMatch = String(attrs || "").match(/\bsrc=(["'])(.*?)\1/i);
    const src = srcMatch?.[2] || "";
    if (src.startsWith(`${APP_SUPPORT_UPLOADS_PATH}/`)) return full;
    return "";
  });
  // Outlook/Word spacer paragraphs (<p>&nbsp;</p>) — display only; <br> counts are preserved.
  return stripEmptyDisplayParagraphs(s);
}

function isPendingSupportMessage(msg) {
  return String(msg?.id || "").startsWith(PENDING_REPLY_ID_PREFIX);
}

function formatSupportPersonLabel(name, email) {
  const displayName = formatPersonDisplayName(name);
  const address = String(email || "").trim().toLowerCase();
  if (displayName && address) return `${displayName} (${address})`;
  return address || displayName || "";
}

const parseJsonSafe = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const parseCcAssigneeIdsFromContext = (contextRaw) => {
  const parsed = parseJsonSafe(contextRaw);
  const ids = parsed?.cc_team_member_ids;
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.map((x) => String(x || "").trim()).filter(Boolean))];
};

const parseCcAssigneeIdsFromColumn = (raw) => {
  const parsed = parseJsonSafe(raw);
  if (!Array.isArray(parsed)) return [];
  return [...new Set(parsed.map((x) => String(x || "").trim()).filter(Boolean))];
};

const mapCcOption = (x) => {
  const source = x?.raw || x;
  const id = String(source?.id || x?.value || "").trim();
  if (!id) return null;
  const type = String(source?.type || "").trim().toLowerCase();
  const baseName = source?.name || id;
  const email = source?.email ? ` - ${source.email}` : "";
  const suffix = type === "customer" ? "Customer" : "Team Member";
  return {
    value: id,
    label: String(source?.text || `${baseName}${email} (${suffix})`).trim(),
    name: baseName,
    email: source?.email || "",
  };
};

/** Back to list — restore last tab from localStorage. */
function supportTicketListBackPath(appId) {
  const id = String(appId || "").trim();
  const base = `/apps/${id}/support/ticket`;
  const tab = normalizeTicketListTab(getLocalStorage(supportTicketListTabStorageKey(id)));
  return tab === "recent" ? base : `${base}?tab=${encodeURIComponent(tab)}`;
}

const AppSupportTicketDetails = () => {
  const navigate = useNavigate();
  const { appId, conversationId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [requesterTicketRows, setRequesterTicketRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [replyAttachmentItems, setReplyAttachmentItems] = useState([]);
  const [sending, setSending] = useState(false);
  const replyFileInputRef = useRef(null);
  const replyQuillRef = useRef(null);
  const replyAttachmentIdRef = useRef(0);
  const replyAttachmentItemsRef = useRef([]);
  const [openTabs, setOpenTabs] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFormatToolbar, setShowFormatToolbar] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiInstructions, setAiInstructions] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const aiInstructionsRef = useRef(null);
  const [replyFormatState, setReplyFormatState] = useState(REPLY_FORMAT_STATE_INITIAL);
  const [replyEditorHeight, setReplyEditorHeight] = useState(readStoredReplyEditorHeight);
  const replyEditorHeightRef = useRef(replyEditorHeight);
  const [profileDrawerEmail, setProfileDrawerEmail] = useState(null);
  const [hoveredRequesterTicketId, setHoveredRequesterTicketId] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    team_member_id: "",
    group_id: "",
    cc_team_member_ids: [],
    context_json: "",
  });
  const [ticketFormErrors, setTicketFormErrors] = useState({});
  const [updatingTicket, setUpdatingTicket] = useState(false);
  const [reopeningTicket, setReopeningTicket] = useState(false);
  const [ccRecipientOptions, setCcRecipientOptions] = useState([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [ticketDetailError, setTicketDetailError] = useState(null);
  const [loadingTicketById, setLoadingTicketById] = useState(false);
  /** Last chosen assignee — used to restore when switching back to a group that includes them. */
  const lastTeamMemberIdRef = useRef("");
  /** Avoid re-running full "open conversation" on every inbox poll (prevents message list reset + scroll jump). */
  const lastOpenedConversationKeyRef = useRef("");
  const singleTicketFetchRef = useRef("");
  const messagesScrollRef = useRef(null);
  const messagesLoadSeqRef = useRef(0);
  const prevMessageIdsSigRef = useRef("");

  const revokeReplyAttachmentPreviews = (items) => {
    (items || []).forEach((x) => {
      if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
    });
  };

  useEffect(() => {
    replyAttachmentItemsRef.current = replyAttachmentItems;
  }, [replyAttachmentItems]);

  useEffect(() => {
    return () => {
      revokeReplyAttachmentPreviews(replyAttachmentItemsRef.current);
    };
  }, []);

  const clearReplyAttachments = useCallback(() => {
    setReplyAttachmentItems((prev) => {
      revokeReplyAttachmentPreviews(prev);
      return [];
    });
    if (replyFileInputRef.current) replyFileInputRef.current.value = "";
  }, []);

  const handleReplyAttachmentInput = (e) => {
    const list = e.target.files;
    if (!list?.length) return;
    const rejected = [];
    const next = [...list]
      .filter((file) => {
        if (isSupportTicketFileAllowed(file)) return true;
        rejected.push(file.name || "file");
        return false;
      })
      .map((file) => {
        const id = ++replyAttachmentIdRef.current;
        const previewUrl = /^image\//i.test(file.type) ? URL.createObjectURL(file) : null;
        return { id, file, previewUrl };
      });
    if (rejected.length) {
      toast.error(
        `Allowed types only: ${SUPPORT_TICKET_ALLOWED_FILE_TYPES.join(", ")}. Skipped: ${rejected.join(", ")}`
      );
    }
    if (next.length) setReplyAttachmentItems((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const removeReplyAttachment = (id) => {
    setReplyAttachmentItems((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  };

  const upsertConversation = useCallback((row) => {
    if (!row?.id) return;
    const key = String(row.id);
    setConversations((prev) => {
      const idx = (prev || []).findIndex((c) => String(c.id) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...row };
        return next;
      }
      return [row, ...(prev || [])];
    });
  }, []);

  const loadConversationById = useCallback(
    (id, { force = false } = {}) => {
      if (!appId || id == null || id === "") return;
      const num = Number(id);
      if (!Number.isFinite(num)) return;
      const key = String(num);
      if (!force && singleTicketFetchRef.current === key) return;
      singleTicketFetchRef.current = key;
      setLoadingTicketById(true);
      setTicketDetailError(null);
      fetchWithAuth(`${Utils.baseurl}application/support/conversations`, {
        method: "POST",
        body: JSON.stringify({ app_id: appId, conversationId: num }),
      })
        .then(async (res) => {
          const result = await res.json().catch(() => ({}));
          if (handleSessionExpired(res, result)) return;
          const row = Array.isArray(result.data) ? result.data[0] : null;
          if (!result.status || !row) {
            setTicketDetailError("not_found");
            return;
          }
          setTicketDetailError(null);
          upsertConversation(row);
        })
        .catch(() => {
          setTicketDetailError("network");
        })
        .finally(() => {
          setLoadingTicketById(false);
        });
    },
    [appId, upsertConversation]
  );

  const refreshCurrentConversation = useCallback(() => {
    if (!conversationId) return;
    singleTicketFetchRef.current = "";
    loadConversationById(conversationId, { force: true });
  }, [conversationId, loadConversationById]);

  const loadTicketMetaOptions = useCallback(async () => {
    if (!appId) return;
    try {
      const rows = await getSupportFieldsForApp(appId);
      setFieldOptions(rows || []);
    } catch {
      // field labels optional
    }
  }, [appId]);

  const fetchGroupOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "support-groups",
        q: search,
        id: ids,
        extra: { app_id: appId },
        mode: "options",
      }),
    [appId]
  );
  const fetchTeamMemberOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "support-team-members",
        q: search,
        id: ids,
        extra: { app_id: appId },
        mode: "options",
      }),
    [appId]
  );

  const hydrateGroupMembers = useCallback(
    async (groupId) => {
      const gid = String(groupId || "").trim();
      if (!gid) {
        setSelectedGroupMembers([]);
        return [];
      }
      const rows = await fetchAjaxListData({
        type: "support-groups",
        id: [gid],
        extra: { app_id: appId, with_members: 1 },
        mode: "rows",
      });
      const members = rows?.[0]?.team_members || [];
      setSelectedGroupMembers(members);
      return members;
    },
    [appId]
  );

  const fieldNameById = useMemo(() => {
    const m = new Map();
    (fieldOptions || []).forEach((f) => {
      const rawId = String(f?.field_id || "").trim();
      const id = normalizeSupportFieldId(rawId);
      const display = String(f?.label || f?.name || "").trim();
      if (!id || !display) return;
      m.set(id, display);
      m.set(id.toLowerCase(), display);
      if (rawId && rawId !== id) {
        m.set(rawId, display);
        m.set(rawId.toLowerCase(), display);
      }
    });
    return m;
  }, [fieldOptions]);

  const resolveFieldLabel = useCallback(
    (fieldId) => {
      const raw = String(fieldId || "").trim();
      if (!raw) return "";
      const normalized = normalizeSupportFieldId(raw);
      const lower = normalized.toLowerCase();
      if (fieldNameById.has(normalized)) return fieldNameById.get(normalized);
      if (fieldNameById.has(lower)) return fieldNameById.get(lower);
      if (fieldNameById.has(raw)) return fieldNameById.get(raw);
      if (fieldNameById.has(raw.toLowerCase())) return fieldNameById.get(raw.toLowerCase());
      return "";
    },
    [fieldNameById]
  );


  const loadMessages = useCallback((conversationId, { silent } = {}) => {
    const seq = ++messagesLoadSeqRef.current;
    fetchWithAuth(`${Utils.baseurl}application/support/messages`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, conversationId }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (seq !== messagesLoadSeqRef.current) return;
        if (handleSessionExpired(res, result)) return;
        if (!result.status) {
          if (!silent) toast.error(result.message || "Failed to load messages");
          return;
        }
        const incoming = Array.isArray(result.data) ? result.data : [];
        setMessages((prev) => {
          const pending = (prev || []).filter(isPendingSupportMessage);
          const byId = new Map();
          for (const m of incoming) {
            if (m?.id != null) byId.set(m.id, m);
          }
          for (const m of pending) {
            byId.set(m.id, m);
          }
          return [...byId.values()].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });
      })
      .catch(() => {
        if (seq !== messagesLoadSeqRef.current) return;
        if (!silent) toast.error("Network error while loading messages");
      });
  }, [appId]);

  useEffect(() => {
    loadTicketMetaOptions();
  }, [appId, loadTicketMetaOptions]);

  useEffect(() => {
    if (!appId || !profileDrawerEmail) {
      setRequesterTicketRows([]);
      return undefined;
    }
    let cancelled = false;
    fetchWithAuth(`${Utils.baseurl}application/support/conversations`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        tab: "all",
        page: 1,
        pageSize: 100,
        requester_email: profileDrawerEmail,
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (cancelled || handleSessionExpired(res, result)) return;
        if (!result.status) return;
        setRequesterTicketRows(result.data || []);
      })
      .catch(() => {
        if (!cancelled) setRequesterTicketRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [appId, profileDrawerEmail]);

  useEffect(() => {
    if (!appId || !conversationId) return undefined;
    const cid = Number(conversationId);
    if (!Number.isFinite(cid)) return undefined;
    const t = setInterval(() => loadMessages(cid, { silent: true }), SUPPORT_TICKETS_POLL_MS);
    return () => clearInterval(t);
  }, [appId, conversationId, loadMessages]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible" || !appId) return;
      if (conversationId) {
        const cid = Number(conversationId);
        if (Number.isFinite(cid)) loadMessages(cid, { silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [appId, conversationId, loadMessages]);

  const onSelectConversation = useCallback((conv) => {
    setReply("");
    clearReplyAttachments();
    setSelected(conv);
    const memberId = conv?.team_member_id || "";
    if (memberId) lastTeamMemberIdRef.current = String(memberId);
    setTicketForm({
      team_member_id: memberId,
      group_id: conv?.group_id || "",
      cc_team_member_ids:
        parseCcAssigneeIdsFromColumn(conv?.cc_team_member_ids_json).length
          ? parseCcAssigneeIdsFromColumn(conv?.cc_team_member_ids_json)
          : parseCcAssigneeIdsFromContext(conv?.context_json),
      context_json: conv?.context_json || "",
    });
    setMessages([]);
    loadMessages(conv.id);
  }, [loadMessages, clearReplyAttachments]);

  useEffect(() => {
    lastOpenedConversationKeyRef.current = "";
    prevMessageIdsSigRef.current = "";
    lastTeamMemberIdRef.current = "";
    singleTicketFetchRef.current = "";
    setTicketDetailError(null);
    setSelected(null);
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    const found = (conversations || []).find((c) => String(c.id) === String(conversationId));
    if (found) {
      setTicketDetailError(null);
      return;
    }
    loadConversationById(conversationId);
  }, [conversationId, conversations, loadConversationById]);

  useEffect(() => {
    if (!conversationId) return;
    const found = (conversations || []).find((c) => String(c.id) === String(conversationId));
    if (!found) return;
    const key = String(conversationId);
    if (lastOpenedConversationKeyRef.current === key) {
      setSelected(found);
      const memberId = found?.team_member_id || "";
      if (memberId) lastTeamMemberIdRef.current = String(memberId);
      setTicketForm({
        team_member_id: memberId,
        group_id: found?.group_id || "",
        cc_team_member_ids:
          parseCcAssigneeIdsFromColumn(found?.cc_team_member_ids_json).length
            ? parseCcAssigneeIdsFromColumn(found?.cc_team_member_ids_json)
            : parseCcAssigneeIdsFromContext(found?.context_json),
        context_json: found?.context_json || "",
      });
      return;
    }
    lastOpenedConversationKeyRef.current = key;
    onSelectConversation(found);
  }, [conversationId, conversations, onSelectConversation]);

  useLayoutEffect(() => {
    const el = messagesScrollRef.current;
    if (!el || !messages.length) return;
    const sig = messages.map((m) => m.id).join("|");
    const prev = prevMessageIdsSigRef.current;
    prevMessageIdsSigRef.current = sig;
    if (!prev) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    if (sig !== prev) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const onUpdateTicket = () => {
    if (!selected || isAppSupportConversationClosed(selected.status)) return;
    const errors = {};
    if (!ticketForm.group_id) errors.group_id = "Please select a Group";
    if (!ticketForm.team_member_id) errors.team_member_id = "Please select a Team member";
    if (Object.keys(errors).length) {
      setTicketFormErrors(errors);
      return;
    }
    setTicketFormErrors({});
    const contextObj = parseJsonSafe(ticketForm.context_json);
    const contextForSave =
      contextObj && typeof contextObj === "object" && !Array.isArray(contextObj)
        ? Array.isArray(contextObj.fields)
          ? { fields: contextObj.fields }
          : contextObj
        : {};
    setUpdatingTicket(true);
    fetchWithAuth(`${Utils.baseurl}application/support/update-ticket`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        conversationId: selected.id,
        team_member_id: ticketForm.team_member_id || null,
        group_id: ticketForm.group_id || null,
        context: contextForSave,
        cc_team_member_ids: ticketForm.cc_team_member_ids || [],
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setUpdatingTicket(false);
        if (handleSessionExpired(res, result)) return;
        if (!result.status) {
          toast.error(result.message || "Failed to update ticket");
          return;
        }
        toast.success("Ticket updated");
        refreshCurrentConversation();
      })
      .catch(() => {
        setUpdatingTicket(false);
        toast.error("Network error while updating ticket");
      });
  };

  const cleanMessageText = (text) => {
    const raw = String(text || "");
    if (!raw.trim()) return "-";
    /** Email clients often leave a line of only - _ = before quoted thread; looks like an <hr> in the bubble */
    const isEmailRuleOnlyLine = (l) =>
      l.length >= 5 && /^[\s_=~*#.\u00B7\u2010-\u2015\u2212-]+$/.test(l) && !/[A-Za-z0-9]/.test(l);
    const lines = raw
      .split("\n")
      .filter((l) => !/^attachments\s*:/i.test(l))
      .filter((l) => !isEmailRuleOnlyLine(String(l || "").trim()));
    return lines.join("\n") || raw;
  };

  const resolveTicketFieldRowLabel = useCallback(
    (fieldItem) => {
      const fId = String(fieldItem?.id || "").trim();
      const rawName = String(fieldItem?.name || "").trim();
      if (rawName && !looksLikeRawSupportFieldId(rawName)) return rawName;
      const fromId = resolveFieldLabel(fId);
      if (fromId) return fromId;
      return resolveFieldLabel(rawName);
    },
    [resolveFieldLabel]
  );

  const renderMergedTicketFields = (contextRaw) => {
    const contextParsed = parseJsonSafe(contextRaw);
    const items = [];
    const seen = new Set();
    const pushItem = (labelRaw, valueRaw) => {
      const label = String(labelRaw || "").trim();
      const labelLower = label.toLowerCase();
      if (labelLower === "subject" || labelLower === "description") return;
      const value =
        valueRaw === undefined || valueRaw === null
          ? ""
          : typeof valueRaw === "string"
            ? valueRaw
            : String(valueRaw);
      if (!label || value === "" || looksLikeRawSupportFieldId(label)) return;
      const dedupeKey = `${label}::${value}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      items.push({ key: label, value });
    };

    if (
      contextParsed &&
      typeof contextParsed === "object" &&
      !Array.isArray(contextParsed) &&
      contextParsed.zendesk?.ticket_id != null &&
      String(contextParsed.zendesk.ticket_id).trim() !== ""
    ) {
      pushItem("Zendesk Ticket ID", contextParsed.zendesk.ticket_id);
    }

    const type = getSupportContextScalar(contextParsed, ["type"]);
    const priority = getSupportContextScalar(contextParsed, ["priority"]);
    if (type) pushItem("Type", type);
    if (priority) pushItem("Priority", priority);

    if (
      contextParsed &&
      typeof contextParsed === "object" &&
      !Array.isArray(contextParsed) &&
      Array.isArray(contextParsed.fields)
    ) {
      contextParsed.fields.forEach((fieldItem) => {
        const label = resolveTicketFieldRowLabel(fieldItem);
        if (!label) return;
        pushItem(label, fieldItem?.value);
      });
    }
    if (!items.length) return <span className="text-muted">-</span>;
    return (
      <div className="row g-2">
        {items.map((item, idx) => (
          <div className="col-12" key={`${item.key}-${idx}`}>
            <div className="p-2 border rounded" style={{ background: "#f8f9fa", padding: "6px 10px" }}>
              <div style={{ fontSize: 10, color: "#6c757d", textTransform: "capitalize" }}>{item.key}</div>
              <div style={{ fontSize: 12 }}>{item.value}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const [assigneeFallbackOption, setAssigneeFallbackOption] = useState(null);

  const groupMemberSelectOptions = useMemo(
    () => mapAjaxRowsToOptions(selectedGroupMembers || []),
    [selectedGroupMembers]
  );

  const groupHasNoTeamMembers =
    Boolean(ticketForm.group_id) && groupMemberSelectOptions.length === 0;

  useEffect(() => {
    const mid = String(ticketForm.team_member_id || "").trim();
    if (!mid || !appId) {
      setAssigneeFallbackOption(null);
      return;
    }
    if (groupMemberSelectOptions.some((o) => String(o.value) === mid)) {
      setAssigneeFallbackOption(null);
      return;
    }
    let cancelled = false;
    fetchTeamMemberOpts({ search: "", ids: [mid] }).then((opts) => {
      if (cancelled) return;
      setAssigneeFallbackOption(opts?.[0] || { value: mid, label: mid });
    });
    return () => {
      cancelled = true;
    };
  }, [ticketForm.team_member_id, groupMemberSelectOptions, appId, fetchTeamMemberOpts]);

  const teamMemberSelectValue =
    groupMemberSelectOptions.find((o) => o.value === String(ticketForm.team_member_id || "")) ||
    assigneeFallbackOption ||
    null;

  useEffect(() => {
    if (!appId || !ticketForm.group_id) {
      setSelectedGroupMembers([]);
      return;
    }
    hydrateGroupMembers(ticketForm.group_id);
  }, [appId, ticketForm.group_id, hydrateGroupMembers]);

  const ccAssigneeSelectOptions = useMemo(() => {
    const fromServer = (ccRecipientOptions || []).filter((x) => x?.value);
    const selectedFallback = (ticketForm.cc_team_member_ids || [])
      .filter((id) => !fromServer.some((opt) => opt.value === id))
      .map((id) => ({ value: id, label: id }));
    return [...fromServer, ...selectedFallback];
  }, [ccRecipientOptions, ticketForm.cc_team_member_ids]);

  const loadCcRecipientOptions = useCallback(async (inputValue = "", selectedIds = []) => {
    if (!appId) return [];
    try {
      setCcLoading(true);
      const rows = await fetchAjaxListData({
        type: "support-cc-recipients",
        q: String(inputValue || "").trim(),
        id: Array.isArray(selectedIds) ? selectedIds : [],
        extra: {
          app_id: appId,
        },
        mode: "rows",
      });
      setCcLoading(false);
      const opts = (rows || []).map(mapCcOption).filter(Boolean);
      setCcRecipientOptions((prev) => {
        const merged = [...(prev || []), ...opts];
        const dedup = new Map();
        merged.forEach((o) => {
          if (o?.value) dedup.set(o.value, o);
        });
        return [...dedup.values()];
      });
      return opts;
    } catch {
      setCcLoading(false);
      return [];
    }
  }, [appId]);

  useEffect(() => {
    const ids = ticketForm.cc_team_member_ids || [];
    if (!ids.length) return;
    const missing = ids.filter((id) => !(ccRecipientOptions || []).some((opt) => String(opt.value) === String(id)));
    if (!missing.length) return;
    loadCcRecipientOptions("", missing);
  }, [ticketForm.cc_team_member_ids, appId, loadCcRecipientOptions, ccRecipientOptions]);

  const replyInFlightRef = useRef(false);

  const buildPendingPanelReply = (pendingId, text) => {
    let email = "";
    let emailFromName = "";
    try {
      const user = JSON.parse(localStorage.getItem(Utils.storageKeys.userData) || "null");
      email = String(user?.email || "").trim().toLowerCase();
      emailFromName =
        [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
        String(user?.name || "").trim();
    } catch (_) {
      /* ignore */
    }
    return {
      id: pendingId,
      conversationId: selected?.id,
      message: text,
      sender_type: 2,
      source_channel: 2,
      email_from: email || null,
      email_from_name: emailFromName || null,
      createdAt: new Date().toISOString(),
      _pending: true,
    };
  };

  const onReply = () => {
    setShowFormatToolbar(false);
    setShowEmojiPicker(false);
    const messageHtml = isEmptyQuillHtml(reply) ? "" : String(reply).trim();
    const attachmentFiles = replyAttachmentItems.map((x) => x.file).filter(Boolean);
    if (!selected || (!messageHtml && !attachmentFiles.length) || replyInFlightRef.current) return;
    replyInFlightRef.current = true;
    const pendingId = `${PENDING_REPLY_ID_PREFIX}${Date.now()}`;
    const pendingDisplay =
      (messageHtml ? quillHtmlToPlainPreview(messageHtml) : "") ||
      (attachmentFiles.length === 1
        ? `Sending attachment: ${attachmentFiles[0].name}`
        : `Sending ${attachmentFiles.length} attachments…`);
    setReply("");
    setSending(true);
    setMessages((prev) =>
      [...(prev || []), buildPendingPanelReply(pendingId, pendingDisplay)].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
    );
    const fd = new FormData();
    fd.append("app_id", appId);
    fd.append("conversationId", selected.id);
    if (messageHtml) fd.append("message", messageHtml);
    attachmentFiles.forEach((file) => fd.append("attachments", file));
    fetchWithAuth(`${Utils.baseurl}application/support/reply`, {
      method: "POST",
      body: fd,
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        replyInFlightRef.current = false;
        setSending(false);
        if (handleSessionExpired(res, result)) {
          setMessages((prev) => (prev || []).filter((m) => m.id !== pendingId));
          setReply(messageHtml);
          return;
        }
        if (!result.status) {
          setMessages((prev) => (prev || []).filter((m) => m.id !== pendingId));
          setReply(messageHtml);
          toast.error(result.message || "Failed to send reply");
          return;
        }
        if (result.emailSent === false) {
          toast.warn(
            result.message ||
              "Reply saved in panel but email was not delivered. Check backend SMTP settings."
          );
        } else {
          toast.success(result.message || "Reply sent successfully");
        }
        clearReplyAttachments();
        const saved = result.data;
        if (saved?.id != null) {
          setMessages((prev) => {
            const next = (prev || []).filter((m) => m.id !== pendingId);
            const byId = new Map(next.map((m) => [m.id, m]));
            byId.set(saved.id, saved);
            return [...byId.values()].sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
          });
        } else {
          setMessages((prev) => (prev || []).filter((m) => m.id !== pendingId));
          loadMessages(selected.id);
        }
        refreshCurrentConversation();
      })
      .catch(() => {
        replyInFlightRef.current = false;
        setSending(false);
        setMessages((prev) => (prev || []).filter((m) => m.id !== pendingId));
        setReply(messageHtml);
        toast.error("Network error while sending reply");
      });
  };

  const openAiPanel = () => {
    setShowFormatToolbar(false);
    setShowEmojiPicker(false);
    setShowAiPanel(true);
  };

  const closeAiPanel = () => {
    setShowAiPanel(false);
    setAiInstructions("");
  };

  const onGenerateAiReply = async () => {
    const points = aiInstructions.trim();
    if (!selected || !points || aiGenerating) return;
    setAiGenerating(true);
    try {
      const res = await fetchWithAuth(`${Utils.baseurl}application/support/ai/generate-reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          app_id: appId,
          conversationId: selected.id,
          instructions: points,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        toast.error(result.message || "Could not generate a reply. Please try again.");
        return;
      }
      const html = plainSupportTextToHtml(result.data?.reply || "");
      if (!html) {
        toast.error("AI returned an empty reply. Please rephrase your points.");
        return;
      }
      // Put the draft into the editor for the agent to review and edit before sending.
      setReply(html);
      closeAiPanel();
      toast.success("Draft ready — review and edit before sending.");
    } catch (_) {
      toast.error("Network error while generating the reply.");
    } finally {
      setAiGenerating(false);
    }
  };

  const onReopenConversation = async () => {
    if (!selected || !isAppSupportConversationClosed(selected.status)) return;
    const { isConfirmed } = await Swal.fire({
      title: "Reopen this ticket?",
      text: "You will be able to update the ticket and send replies again.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, reopen",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) return;
    setReopeningTicket(true);
    fetchWithAuth(`${Utils.baseurl}application/support/reopen`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, conversationId: selected.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setReopeningTicket(false);
        if (handleSessionExpired(res, result)) return;
        if (!result.status) {
          toast.error(result.message || "Failed to reopen conversation");
          return;
        }
        toast.success("Ticket reopened — you can update and reply again.");
        refreshCurrentConversation();
      })
      .catch(() => {
        setReopeningTicket(false);
        toast.error("Network error while reopening conversation");
      });
  };

  const onCloseConversation = async () => {
    if (!selected) return;
    const { isConfirmed } = await Swal.fire({
      title: "Close this conversation?",
      text: "You can reopen it anytime with the Reopen button.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6efd",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, close",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) return;
    fetchWithAuth(`${Utils.baseurl}application/support/close`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, conversationId: selected.id }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        if (handleSessionExpired(res, result)) return;
        if (!result.status) {
          toast.error(result.message || "Failed to close conversation");
          return;
        }
        toast.success("Conversation closed");
        refreshCurrentConversation();
      })
      .catch(() => toast.error("Network error while closing conversation"));
  };

  const ticketStatusBadgeClass = (s) => {
    return getAppSupportConversationStatusDisplay(s)?.badgeClass || "badge bg-success";
  };

  const formatTicketStatus = (s) => {
    return getAppSupportConversationStatusDisplay(s)?.label || "Open";
  };

  const participantNameByEmail = useMemo(() => {
    const map = new Map();
    const add = (email, name) => {
      const address = String(email || "").trim().toLowerCase();
      const displayName = String(name || "").trim();
      if (!address || !displayName || map.has(address)) return;
      map.set(address, displayName);
    };
    (ccRecipientOptions || []).forEach((row) => add(row.email, row.name));
    (selectedGroupMembers || []).forEach((row) => add(row.email, row.name));
    if (selected?.requester_email) add(selected.requester_email, selected.requester_name);
    try {
      const user = JSON.parse(localStorage.getItem(Utils.storageKeys.userData) || "null");
      if (user?.email) {
        const displayName =
          [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
          String(user.name || "").trim();
        add(user.email, displayName);
      }
    } catch (_) {
      /* ignore */
    }
    return map;
  }, [ccRecipientOptions, selectedGroupMembers, selected]);

  const resolveMessageSenderLabel = (msg) => {
    if (msg?._pending) return "Sending…";
    const senderType = normalizeAppSupportMessageSenderType(msg?.sender_type);
    const storedName = String(msg?.email_from_name || "").trim();
    const parsed = parseSupportMailboxAddress(msg?.email_from);
    const email = parsed.email || String(msg?.email_from || "").trim().toLowerCase();
    const name = storedName || parsed.name || participantNameByEmail.get(email) || "";

    if (senderType === 1) {
      const requesterEmail = String(selected?.requester_email || email || "").trim().toLowerCase();
      const requesterName = name || String(selected?.requester_name || "").trim();
      return formatSupportPersonLabel(requesterName, requesterEmail) || "App User";
    }

    if (senderType === 2) {
      const isSmtpMailbox = !email || /@notifysphere\.in$/i.test(email);
      if (isSmtpMailbox) {
        try {
          const user = JSON.parse(localStorage.getItem(Utils.storageKeys.userData) || "null");
          const loginEmail = String(user?.email || "").trim().toLowerCase();
          if (loginEmail) {
            const loginName =
              [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
              String(user.name || "").trim();
            return formatSupportPersonLabel(loginName, loginEmail) || "Team Member";
          }
        } catch (_) {
          /* ignore */
        }
      }
    }

    return formatSupportPersonLabel(name, email) || (senderType === 3 ? "Email Reply" : "Team Member");
  };

  const isTicketClosed = isAppSupportConversationClosed(selected?.status);

  useEffect(() => {
    if (!appId) return;
    setOpenTabs(loadSupportOpenTabs(appId));
  }, [appId]);

  useEffect(() => {
    if (!appId || !selected?.id) return;
    setOpenTabs(touchSupportOpenTab(appId, selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tab label fields listed above
  }, [appId, selected?.id, selected?.ticket_ref, selected?.subject]);

  useEffect(() => {
    replyEditorHeightRef.current = replyEditorHeight;
  }, [replyEditorHeight]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest(".ns-support-emoji-wrap")) setShowEmojiPicker(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showEmojiPicker]);

  useEffect(() => {
    if (!showFormatToolbar) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest(".ns-support-reply-format-wrap")) setShowFormatToolbar(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showFormatToolbar]);

  useEffect(() => {
    if (!showAiPanel) return undefined;
    const onDoc = (e) => {
      // Keep typed points if the agent clicks away; just hide the panel.
      if (!e.target.closest(".ns-support-ai-wrap")) setShowAiPanel(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showAiPanel]);

  const replyAgentLabel = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem(Utils.storageKeys.userData) || "null");
      if (!user) return "";
      return (
        [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
        String(user.name || "").trim() ||
        String(user.email || "").trim()
      );
    } catch {
      return "";
    }
  }, []);

  const switchToTab = (tabId) => {
    if (!appId || String(tabId) === String(conversationId)) return;
    navigate(`/apps/${appId}/support/ticket/details/${tabId}`);
  };

  const closeOpenTab = (tabId, e) => {
    e?.stopPropagation();
    if (!appId) return;
    const closedIndex = openTabs.findIndex((t) => String(t.id) === String(tabId));
    const remaining = removeSupportOpenTab(appId, tabId);
    setOpenTabs(remaining);
    if (String(tabId) !== String(conversationId)) return;
    if (remaining.length === 0) {
      navigate(supportTicketListBackPath(appId));
      return;
    }
    const nextIndex =
      closedIndex >= 0 && closedIndex < remaining.length ? closedIndex : remaining.length - 1;
    navigate(`/apps/${appId}/support/ticket/details/${remaining[nextIndex].id}`);
  };

  const getReplyEditor = useCallback(() => replyQuillRef.current?.getEditor?.(), []);

  const syncReplyFormatState = useCallback(() => {
    const editor = getReplyEditor();
    if (!editor) return;
    const range = editor.getSelection();
    const formats = range ? editor.getFormat(range) : {};
    setReplyFormatState({
      bold: !!formats.bold,
      italic: !!formats.italic,
      underline: !!formats.underline,
      strike: !!formats.strike,
      listBullet: formats.list === "bullet",
      listOrdered: formats.list === "ordered",
      blockquote: !!formats.blockquote,
      codeBlock: !!formats["code-block"],
    });
  }, [getReplyEditor]);

  const applyReplyFormat = useCallback(
    (name, value = true) => {
      const editor = getReplyEditor();
      if (!editor) return;
      editor.focus();
      const range = editor.getSelection(true);
      if (!range) return;
      const formats = editor.getFormat(range);
      const isBlock = name === "blockquote" || name === "code-block";
      const isActive = !!formats[name];
      const next = isActive ? false : value;
      if (isBlock) {
        editor.formatLine(range.index, range.length || 1, name, next);
      } else {
        editor.format(name, next);
      }
      syncReplyFormatState();
    },
    [getReplyEditor, syncReplyFormatState]
  );

  const insertReplyListPrefix = useCallback(
    (listType) => {
      const editor = getReplyEditor();
      if (!editor) return;
      editor.focus();
      const range = editor.getSelection(true);
      if (!range) return;
      const formats = editor.getFormat(range);
      const isActive = formats.list === listType;
      editor.formatLine(range.index, range.length || 1, "list", isActive ? false : listType);
      syncReplyFormatState();
    },
    [getReplyEditor, syncReplyFormatState]
  );

  const beginReplyResize = useCallback(
    (e) => {
      if (sending) return;
      e.preventDefault();
      const startY = e.touches?.[0]?.clientY ?? e.clientY;
      const startHeight = replyEditorHeightRef.current;
      document.body.classList.add("ns-support-reply-resizing");

      const onMove = (moveEvent) => {
        const clientY = moveEvent.touches?.[0]?.clientY ?? moveEvent.clientY;
        const delta = startY - clientY;
        const next = Math.min(
          REPLY_EDITOR_HEIGHT_MAX,
          Math.max(REPLY_EDITOR_HEIGHT_MIN, startHeight + delta)
        );
        setReplyEditorHeight(next);
        moveEvent.preventDefault();
      };

      const onEnd = () => {
        document.body.classList.remove("ns-support-reply-resizing");
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onEnd);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
        try {
          localStorage.setItem(
            REPLY_EDITOR_HEIGHT_STORAGE_KEY,
            String(replyEditorHeightRef.current)
          );
        } catch {
          /* ignore */
        }
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onEnd);
      document.addEventListener("touchmove", onMove, { passive: false });
      document.addEventListener("touchend", onEnd);
    },
    [sending]
  );

  const toggleFormatToolbar = useCallback(() => {
    setShowEmojiPicker(false);
    setShowFormatToolbar((v) => !v);
  }, []);

  const toggleEmojiPicker = useCallback(() => {
    setShowFormatToolbar(false);
    setShowEmojiPicker((v) => !v);
  }, []);

  const openReplyAttachmentPicker = useCallback(() => {
    setShowFormatToolbar(false);
    setShowEmojiPicker(false);
    replyFileInputRef.current?.click();
  }, []);

  const handleEmojiSelect = useCallback(
    (emojiData) => {
      const emoji = emojiData?.emoji || "";
      if (!emoji) return;
      const editor = getReplyEditor();
      if (!editor) {
        setReply((prev) => prev + emoji);
        return;
      }
      editor.focus();
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, emoji, "user");
      editor.setSelection(index + emoji.length, 0);
    },
    [getReplyEditor]
  );

  useEffect(() => {
    const editor = getReplyEditor();
    if (!editor) return undefined;
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        onReply();
      }
    };
    editor.root.addEventListener("keydown", onKeyDown);
    return () => editor.root.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bind when editor mounts
  }, [getReplyEditor, selected?.id, sending]);

  useEffect(() => {
    const editor = getReplyEditor();
    if (!editor) return undefined;
    const onSelectionChange = () => syncReplyFormatState();
    const onTextChange = () => syncReplyFormatState();
    editor.on("selection-change", onSelectionChange);
    editor.on("text-change", onTextChange);
    syncReplyFormatState();
    return () => {
      editor.off("selection-change", onSelectionChange);
      editor.off("text-change", onTextChange);
    };
  }, [getReplyEditor, syncReplyFormatState, selected?.id, sending]);

  const attachFileHint = `Max 10MB per file. Allowed: ${SUPPORT_TICKET_ALLOWED_FILE_TYPES.join(", ")}`;

  const requesterProfiles = useMemo(() => {
    const map = new Map();
    const add = (c) => {
      const email = String(c?.requester_email || "").trim().toLowerCase();
      if (!email || map.has(email)) return;
      map.set(email, {
        email,
        name: String(c?.requester_name || "").trim(),
        initials: getAvatarInitials(c?.requester_name, email),
      });
    };
    add(selected);
    for (const c of conversations || []) add(c);
    for (const c of requesterTicketRows || []) add(c);
    return [...map.values()];
  }, [selected, conversations, requesterTicketRows]);

  const railRequesters = useMemo(() => {
    const email = String(selected?.requester_email || "").trim().toLowerCase();
    if (!email) return [];
    const fromList = requesterProfiles.find((p) => p.email === email);
    if (fromList) return [fromList];
    return [
      {
        email,
        name: String(selected?.requester_name || "").trim(),
        initials: getAvatarInitials(selected?.requester_name, email),
      },
    ];
  }, [selected, requesterProfiles]);

  const activeRequesterProfile = useMemo(
    () => requesterProfiles.find((p) => p.email === profileDrawerEmail) || null,
    [requesterProfiles, profileDrawerEmail]
  );

  const requesterTickets = useMemo(() => {
    if (!profileDrawerEmail) return [];
    return (requesterTicketRows || [])
      .filter((c) => String(c.requester_email || "").trim().toLowerCase() === profileDrawerEmail)
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [requesterTicketRows, profileDrawerEmail]);

  const hoveredRequesterTicket = useMemo(
    () => requesterTickets.find((t) => String(t.id) === String(hoveredRequesterTicketId)) || null,
    [requesterTickets, hoveredRequesterTicketId]
  );

  const profileDrawerListRef = useRef(null);
  const [requesterPreviewPos, setRequesterPreviewPos] = useState(null);

  const updateRequesterTicketPreviewPosition = useCallback((anchorEl) => {
    if (!anchorEl || typeof window === "undefined") {
      setRequesterPreviewPos(null);
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    const drawer = anchorEl.closest(".ns-support-profile-drawer");
    const drawerRect = drawer?.getBoundingClientRect();
    const width = Math.min(300, Math.floor(window.innerWidth * 0.42));
    const gap = 12;
    let left = (drawerRect?.left ?? rect.right) - width - gap;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const maxHeight = 280;
    let top = rect.top;
    top = Math.max(8, Math.min(top, window.innerHeight - maxHeight - 8));
    setRequesterPreviewPos({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!hoveredRequesterTicketId || !profileDrawerListRef.current) {
      setRequesterPreviewPos(null);
      return;
    }
    const anchor = profileDrawerListRef.current.querySelector(
      `[data-requester-ticket-row="${hoveredRequesterTicketId}"]`
    );
    if (anchor) updateRequesterTicketPreviewPosition(anchor);
    else setRequesterPreviewPos(null);
  }, [hoveredRequesterTicketId, updateRequesterTicketPreviewPosition, requesterTickets]);

  useEffect(() => {
    if (!hoveredRequesterTicketId || !profileDrawerListRef.current) return undefined;
    const list = profileDrawerListRef.current;
    const sync = () => {
      const anchor = list.querySelector(`[data-requester-ticket-row="${hoveredRequesterTicketId}"]`);
      if (anchor) updateRequesterTicketPreviewPosition(anchor);
    };
    list.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      list.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [hoveredRequesterTicketId, updateRequesterTicketPreviewPosition]);

  const renderRequesterTicketPreviewCard = (conv) => {
    if (!conv) return null;
    const type = getSupportContextScalar(conv.context_json, ["type"]);
    const priority = getSupportContextScalar(conv.context_json, ["priority"]);
    const lastSnippet = conv.last_message ? String(conv.last_message).slice(0, 120) : null;
    return (
      <>
        <div className="ns-support-requester-ticket__preview-ref">
          {conv.ticket_ref || `#${conv.id}`}
        </div>
        <div className="ns-support-requester-ticket__preview-subject">
          {conv.subject || "(No subject)"}
        </div>
        <dl className="ns-support-requester-ticket__preview-meta">
          <div>
            <dt>Status</dt>
            <dd>{formatTicketStatus(conv.status)}</dd>
          </div>
          {conv.createdAt ? (
            <div>
              <dt>Created</dt>
              <dd>{formatDateTime(conv.createdAt)}</dd>
            </div>
          ) : null}
          {type ? (
            <div>
              <dt>Type</dt>
              <dd>{type}</dd>
            </div>
          ) : null}
          {priority ? (
            <div>
              <dt>Priority</dt>
              <dd>{priority}</dd>
            </div>
          ) : null}
        </dl>
        {lastSnippet ? (
          <p className="ns-support-requester-ticket__preview-snippet">{lastSnippet}</p>
        ) : null}
      </>
    );
  };

  const openRequesterTicket = (conv) => {
    if (!conv?.id || !appId) return;
    touchSupportOpenTab(appId, conv);
    setOpenTabs(touchSupportOpenTab(appId, conv));
    setProfileDrawerEmail(null);
    navigate(`/apps/${appId}/support/ticket/details/${conv.id}`);
  };

  const getMessageAvatarClass = (msg) => {
    const t = normalizeAppSupportMessageSenderType(msg?.sender_type);
    if (t === 1) return "ns-support-msg__avatar ns-support-msg__avatar--customer";
    if (t === 3) return "ns-support-msg__avatar ns-support-msg__avatar--email";
    return "ns-support-msg__avatar";
  };

  const getMessageInitials = (msg) => {
    const parsed = parseSupportMailboxAddress(msg?.email_from);
    const name = String(msg?.email_from_name || "").trim() || parsed.name;
    return getAvatarInitials(name, parsed.email);
  };

  const renderMessageBlock = (msg, prevMsg) => {
    const parsed = extractFilesFromText(msg.message, Utils.baseurl);
    const displayText = cleanMessageText(parsed.cleanText);
    const displayIsHtml = looksLikeSupportHtmlMessage(displayText);
    const displayHtml = sanitizeSupportMessageHtmlForDisplay(
      displayIsHtml ? displayText : plainSupportTextToHtml(displayText)
    );
    const atts = parsed.attachments || [];
    const senderLabel = resolveMessageSenderLabel(msg);
    const isOutgoing = normalizeAppSupportMessageSenderType(msg.sender_type) === 2;
    const sameSenderAsPrev =
      prevMsg &&
      normalizeAppSupportMessageSenderType(prevMsg.sender_type) ===
        normalizeAppSupportMessageSenderType(msg.sender_type) &&
      String(prevMsg.email_from || "").trim().toLowerCase() ===
        String(msg.email_from || "").trim().toLowerCase();
    return (
      <div
        key={msg.id}
        className={`ns-support-msg${isOutgoing ? " ns-support-msg--out" : ""}${sameSenderAsPrev ? " ns-support-msg--grouped" : ""}${msg._pending ? " ns-support-msg--pending" : ""}`}
      >
        <div className={getMessageAvatarClass(msg)} aria-hidden>
          {getMessageInitials(msg)}
        </div>
        <div className="ns-support-msg__body">
          <div
            className={`ns-support-msg__bubble${msg._pending ? " ns-support-msg__bubble--pending" : ""}`}
          >
            <div className="ns-support-msg__meta">
              <span className="ns-support-msg__name">{senderLabel}</span>
              <span className="ns-support-msg__time">
                {msg.createdAt ? formatDateTime(msg.createdAt) : ""}
              </span>
            </div>
            {displayHtml ? (
              <div
                className="ns-support-msg__text ns-support-msg__text--html"
                dangerouslySetInnerHTML={{ __html: displayHtml }}
              />
            ) : (
              <div className="ns-support-msg__text ns-support-msg__text--plain">{displayText}</div>
            )}
            {atts.length ? (
              <div className="ns-support-msg__attachments mt-2">
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6c757d", marginBottom: 6 }}>
                  Attachments
                </div>
                <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                  {atts.map((att, idx) => {
                    const displayName = normalizeFileName(att.fileName);
                    const { ext, isImg, isPdf } = resolveFileKind(att.fileName, att.url);
                    return (
                      <button
                        key={`${att.relativeUrl || att.url}-${idx}`}
                        type="button"
                        className="btn btn-light border"
                        onClick={() =>
                          setAttachmentPreview({
                            ...att,
                            fileName: displayName || att.fileName,
                            ext,
                            isImg,
                            isPdf,
                          })
                        }
                        style={{
                          width: 160,
                          textAlign: "left",
                          padding: 8,
                          borderRadius: 10,
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            height: 84,
                            borderRadius: 8,
                            overflow: "hidden",
                            border: "1px solid #e9ecef",
                            background: "#f8f9fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isImg ? (
                            <img
                              src={att.url}
                              alt={displayName || att.fileName}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#495057" }}>
                              {isPdf ? "PDF" : (ext || "FILE").toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            marginTop: 6,
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={att.fileName}
                        >
                          {displayName || att.fileName || "Attachment"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1 className="mb-1">
              {selected
                ? `${selected.ticket_ref || `Ticket #${selected.id}`}${selected.subject ? ` - ${selected.subject}` : ""}`
                : "Conversation Details"}
              {selected ? (
                <span className={`ms-3 ${ticketStatusBadgeClass(selected.status)}`} style={{ fontSize: "0.45em", verticalAlign: "middle" }}>
                  {formatTicketStatus(selected.status)}
                </span>
              ) : null}
            </h1>
          </div>
          <div className="add_app">
            <button
              type="button"
              className="add_app_btn_secondary btn_theme_secondary"
              onClick={() => navigate(supportTicketListBackPath(appId))}
            >
              <span>Back</span>
            </button>
          </div>
        </div>
        <div className="ns-support-details-page">
          {openTabs.length > 0 ? (
            <div className="ns-support-tabs" role="tablist">
              {openTabs.map((tab) => {
                const active = String(tab.id) === String(conversationId);
                const { subject, ref, title } = supportOpenTabLabelParts(tab);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`ns-support-tabs__tab${active ? " ns-support-tabs__tab--active" : ""}`}
                    onClick={() => switchToTab(tab.id)}
                    title={title}
                  >
                    <span className="ns-support-tabs__label">
                      {subject ? (
                        <span className="ns-support-tabs__subject">{subject}</span>
                      ) : null}
                      <span className="ns-support-tabs__ref">{ref}</span>
                    </span>
                    <span
                      className="ns-support-tabs__close"
                      role="button"
                      tabIndex={0}
                      aria-label="Close tab"
                      onClick={(e) => closeOpenTab(tab.id, e)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") closeOpenTab(tab.id, e);
                      }}
                    >
                      <BsX size={14} />
                    </span>
                  </button>
                );
              })}
              {railRequesters.length > 0 ? (
                <div className="ns-support-details-rail" aria-label="Requester tickets">
                  {railRequesters.map((p) => {
                    const active = profileDrawerEmail === p.email;
                    return (
                      <button
                        key={p.email}
                        type="button"
                        className={`ns-support-rail-btn${active ? " ns-support-rail-btn--active" : ""}`}
                        style={{ background: "#0ea5e9" }}
                        title={p.name || p.email || "Requester"}
                        onClick={() => setProfileDrawerEmail(active ? null : p.email)}
                      >
                        {p.initials}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="ns-support-details-grid">
          <aside className="ns-support-details-left">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center py-2" style={{ fontSize: 14 }}>
                <span>Ticket Details</span>
                {selected && !isTicketClosed && can("support.tickets.manage") ? (
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={onCloseConversation}>
                    Close
                  </button>
                ) : null}
                {selected && isTicketClosed && can("support.tickets.manage") ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={onReopenConversation}
                    disabled={reopeningTicket}
                  >
                    {reopeningTicket ? "Reopening..." : "Reopen"}
                  </button>
                ) : null}
              </div>
              <div className="card-body py-2" style={{ fontSize: 13 }}>
                {!selected ? (
                  ticketDetailError === "not_found" ? (
                    <div className="text-danger">Ticket not found or you do not have access to it.</div>
                  ) : ticketDetailError === "network" ? (
                    <div className="text-danger">
                      Could not reach the API server. Ensure the backend is running and{" "}
                      <code>REACT_APP_BASE_URL</code> points to it (e.g. http://localhost:3008/).
                    </div>
                  ) : loadingTicketById ? (
                    <div className="text-muted">Loading ticket details...</div>
                  ) : (
                    <div className="text-muted">Loading ticket details...</div>
                  )
                ) : (
                  <>
                    <div className="row g-2 mb-2">
                      <div className="col-12">
                        <label className="form-label mb-1"><strong>Group</strong></label>
                        <AsyncSearchSelect
                          classNamePrefix="ns-support-select"
                          isDisabled={isTicketClosed}
                          isClearable
                          placeholder="Type to search groups…"
                          fetchOptions={fetchGroupOpts}
                          value={ticketForm.group_id || null}
                          onChange={async (opt) => {
                            const newGroupId = opt?.value || "";
                            setTicketFormErrors((e) => ({ ...e, group_id: undefined }));
                            const newMembers = newGroupId ? await hydrateGroupMembers(newGroupId) : [];
                            setTicketForm((p) => {
                              if (!newGroupId || !newMembers.length) {
                                return { ...p, group_id: newGroupId, team_member_id: "" };
                              }
                              const candidate =
                                p.team_member_id ||
                                lastTeamMemberIdRef.current ||
                                selected?.team_member_id ||
                                "";
                              if (!candidate) {
                                return { ...p, group_id: newGroupId, team_member_id: "" };
                              }
                              const exists = newMembers.some((m) => String(m.team_member_id) === String(candidate));
                              const nextMemberId = exists ? String(candidate) : "";
                              if (nextMemberId) lastTeamMemberIdRef.current = nextMemberId;
                              return { ...p, group_id: newGroupId, team_member_id: nextMemberId };
                            });
                          }}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                        {ticketFormErrors.group_id && <small className="text-danger">{ticketFormErrors.group_id}</small>}
                      </div>
                      <div className="col-12">
                        <label className="form-label mb-1"><strong>Team member</strong></label>
                        {ticketForm.group_id ? (
                          <Select
                            classNamePrefix="ns-support-select"
                            isDisabled={isTicketClosed || groupHasNoTeamMembers}
                            isClearable
                            placeholder="Unassigned"
                            options={groupMemberSelectOptions}
                            value={teamMemberSelectValue}
                            onChange={(opt) => {
                              setTicketFormErrors((e) => ({ ...e, team_member_id: undefined }));
                              const nextId = opt?.value || "";
                              if (nextId) lastTeamMemberIdRef.current = String(nextId);
                              setTicketForm((p) => ({ ...p, team_member_id: nextId }));
                            }}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        ) : (
                          <AsyncSearchSelect
                            classNamePrefix="ns-support-select"
                            isDisabled={isTicketClosed}
                            isClearable
                            placeholder="Type to search team members…"
                            fetchOptions={fetchTeamMemberOpts}
                            value={ticketForm.team_member_id || null}
                            onChange={(opt) => {
                              setTicketFormErrors((e) => ({ ...e, team_member_id: undefined }));
                              const nextId = opt?.value || "";
                              if (nextId) lastTeamMemberIdRef.current = String(nextId);
                              setTicketForm((p) => ({ ...p, team_member_id: nextId }));
                            }}
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        )}
                        {ticketFormErrors.team_member_id && <small className="text-danger">{ticketFormErrors.team_member_id}</small>}
                      </div>
                      <div className="col-12">
                        <label className="form-label mb-1"><strong>CC</strong></label>
                        <AsyncSelect
                          isMulti
                          isDisabled={isTicketClosed}
                          classNamePrefix="ns-support-cc-select"
                          cacheOptions
                          defaultOptions={ccAssigneeSelectOptions}
                          loadOptions={loadCcRecipientOptions}
                          isLoading={ccLoading}
                          value={ccAssigneeSelectOptions.filter((opt) =>
                            (ticketForm.cc_team_member_ids || []).includes(opt.value)
                          )}
                          onMenuOpen={() => {
                            if (!ccAssigneeSelectOptions.length) loadCcRecipientOptions("");
                          }}
                          onChange={(selected) => {
                            const selectedIds = (selected || [])
                              .map((opt) => String(opt.value || "").trim())
                              .filter(Boolean);
                            setTicketForm((p) => ({ ...p, cc_team_member_ids: selectedIds }));
                          }}
                          placeholder="Select CC recipients..."
                        />
                      </div>
                    </div>
                    {can("support.tickets.manage") ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary mb-3"
                        onClick={onUpdateTicket}
                        disabled={updatingTicket || isTicketClosed}
                      >
                        {updatingTicket ? "Updating..." : "Update Ticket"}
                      </button>
                    ) : null}
                    <div className="mb-2">
                      <label className="form-label mb-1"><strong>Ticket Fields</strong></label>
                      {renderMergedTicketFields(ticketForm.context_json)}
                    </div>
                  </>
                )}
              </div>
            </div>
          </aside>
          <main className="ns-support-details-center">
            <div className="ns-support-thread-panel">
              <div className="ns-support-thread-panel__header">Messages</div>
              <div ref={messagesScrollRef} className="ns-support-messages-scroll">
                {!selected ? (
                  <div className="text-muted">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="text-muted">No messages.</div>
                ) : (
                  messages.map((msg, i) => renderMessageBlock(msg, messages[i - 1]))
                )}
              </div>
              {selected && !isAppSupportConversationClosed(selected.status) ? (
                <>
                <div
                  className="ns-support-reply-resize-handle"
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label="Resize reply area"
                  onMouseDown={beginReplyResize}
                  onTouchStart={beginReplyResize}
                />
                <div className="ns-support-reply-wrap">
                  <input
                    ref={replyFileInputRef}
                    type="file"
                    className="d-none"
                    multiple
                    accept={buildSupportTicketFileAcceptAttribute()}
                    onChange={handleReplyAttachmentInput}
                  />
                  <div className="ns-support-reply-header">
                    <p className="ns-support-reply-to">
                      Reply
                      {replyAgentLabel ? ` · ${replyAgentLabel}` : ""}
                    </p>
                  </div>
                  {replyAttachmentItems.length > 0 ? (
                    <div className="ns-support-reply-composer__attachments">
                      {replyAttachmentItems.map((item) => {
                        const name = item.file?.name || "file";
                        const ext = (name.split(".").pop() || "").toLowerCase();
                        const isPdf = item.file?.type === "application/pdf" || ext === "pdf";
                        return (
                          <div key={item.id} className="ns-support-reply-composer__chip">
                            <div className="ns-support-reply-composer__chip-thumb">
                              {item.previewUrl ? (
                                <img alt="" src={item.previewUrl} />
                              ) : isPdf ? (
                                <span className="text-danger fw-bold" style={{ fontSize: 11 }}>
                                  PDF
                                </span>
                              ) : (
                                <BsFileEarmark className="text-secondary" style={{ fontSize: 18 }} aria-hidden />
                              )}
                            </div>
                            <span className="ns-support-reply-composer__chip-name" title={name}>
                              {name}
                            </span>
                            <button
                              type="button"
                              className="ns-support-reply-composer__chip-remove"
                              aria-label={`Remove ${name}`}
                              onClick={() => removeReplyAttachment(item.id)}
                              disabled={sending}
                            >
                              <BsX size={16} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  <div
                    className={`ns-support-reply-editor${sending ? " ns-support-reply-editor--disabled" : ""}`}
                    style={{ "--ns-reply-editor-height": `${replyEditorHeight}px` }}
                  >
                    <ReactQuill
                      ref={replyQuillRef}
                      theme="snow"
                      value={reply}
                      onChange={setReply}
                      modules={REPLY_QUILL_MODULES}
                      formats={REPLY_QUILL_FORMATS}
                      placeholder="Write your reply… (Ctrl+Enter to send)"
                      readOnly={sending}
                    />
                  </div>
                  <div className="ns-support-reply-footer">
                    <div className="ns-support-reply-tools">
                      <div className="ns-support-ai-wrap">
                        <button
                          type="button"
                          className={`ns-support-reply-format__btn ns-support-ai-btn${showAiPanel ? " ns-support-ai-btn--active" : ""}`}
                          title="Help me write — describe your reply, AI drafts it"
                          disabled={sending}
                          onClick={() => (showAiPanel ? closeAiPanel() : openAiPanel())}
                        >
                          <BsStars size={15} />
                          <span className="ns-support-ai-btn__label">Help me write</span>
                        </button>
                        {showAiPanel ? (
                          <div className="ns-support-ai-panel" role="dialog" aria-label="Help me write">
                            <div className="ns-support-ai-panel__head">
                              <BsStars size={14} aria-hidden />
                              <span>What do you want to say?</span>
                              <button
                                type="button"
                                className="ns-support-ai-panel__close"
                                title="Close"
                                onClick={closeAiPanel}
                              >
                                <BsX size={18} />
                              </button>
                            </div>
                            <textarea
                              ref={aiInstructionsRef}
                              className="ns-support-ai-panel__input"
                              placeholder="A few rough points (grammar doesn't matter)… e.g. issue fixed in next release, will update by friday"
                              rows={3}
                              value={aiInstructions}
                              disabled={aiGenerating}
                              autoFocus
                              onChange={(e) => setAiInstructions(e.target.value)}
                              onKeyDown={(e) => {
                                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                                  e.preventDefault();
                                  onGenerateAiReply();
                                }
                              }}
                            />
                            <div className="ns-support-ai-panel__foot">
                              <span className="ns-support-ai-panel__hint">
                                Uses the ticket thread as context. Review the draft before sending.
                              </span>
                              {can("support.tickets.manage") ? (
                                <button
                                  type="button"
                                  className="btn_theme_primary ns-support-ai-panel__generate"
                                  disabled={aiGenerating || !aiInstructions.trim()}
                                  onClick={onGenerateAiReply}
                                >
                                  {aiGenerating ? "Generating…" : "Generate"}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className="ns-support-reply-format-wrap">
                        {showFormatToolbar ? (
                          <div className="ns-support-reply-format-panel" role="toolbar" aria-label="Text formatting">
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.bold)}
                              title="Bold"
                              aria-pressed={replyFormatState.bold}
                              disabled={sending}
                              onClick={() => applyReplyFormat("bold")}
                            >
                              <BsTypeBold size={15} />
                            </button>
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.italic)}
                              title="Italic"
                              aria-pressed={replyFormatState.italic}
                              disabled={sending}
                              onClick={() => applyReplyFormat("italic")}
                            >
                              <BsTypeItalic size={15} />
                            </button>
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.underline)}
                              title="Underline"
                              aria-pressed={replyFormatState.underline}
                              disabled={sending}
                              onClick={() => applyReplyFormat("underline")}
                            >
                              <BsTypeUnderline size={15} />
                            </button>
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.strike)}
                              title="Strikethrough"
                              aria-pressed={replyFormatState.strike}
                              disabled={sending}
                              onClick={() => applyReplyFormat("strike")}
                            >
                              <BsTypeStrikethrough size={15} />
                            </button>
                            <span className="ns-support-reply-format__sep" aria-hidden />
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.listBullet)}
                              title="Bullet list"
                              aria-pressed={replyFormatState.listBullet}
                              disabled={sending}
                              onClick={() => insertReplyListPrefix("bullet")}
                            >
                              <BsListUl size={15} />
                            </button>
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.listOrdered)}
                              title="Numbered list"
                              aria-pressed={replyFormatState.listOrdered}
                              disabled={sending}
                              onClick={() => insertReplyListPrefix("ordered")}
                            >
                              <BsListOl size={15} />
                            </button>
                            <span className="ns-support-reply-format__sep" aria-hidden />
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.blockquote)}
                              title="Quote"
                              aria-pressed={replyFormatState.blockquote}
                              disabled={sending}
                              onClick={() => applyReplyFormat("blockquote")}
                            >
                              <BsQuote size={15} />
                            </button>
                            <button
                              type="button"
                              className={replyFormatBtnClass(replyFormatState.codeBlock)}
                              title="Code"
                              aria-pressed={replyFormatState.codeBlock}
                              disabled={sending}
                              onClick={() => applyReplyFormat("code-block")}
                            >
                              <BsCode size={15} />
                            </button>
                          </div>
                        ) : null}
                        <button
                          type="button"
                          className={`ns-support-reply-format__btn ns-support-reply-tools__text${showFormatToolbar ? " ns-support-reply-tools__text--active" : ""}`}
                          title="Formatting"
                          disabled={sending}
                          onClick={toggleFormatToolbar}
                        >
                          T
                        </button>
                      </div>
                      <div className="ns-support-emoji-wrap">
                        <button
                          type="button"
                          className="ns-support-reply-format__btn"
                          title="Emoji"
                          disabled={sending}
                          onClick={toggleEmojiPicker}
                        >
                          <BsEmojiSmile size={15} />
                        </button>
                        {showEmojiPicker ? (
                          <div
                            className="ns-support-emoji-picker-popover"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <EmojiPicker onEmojiClick={handleEmojiSelect} width={320} height={380} previewConfig={{ showPreview: false }} />
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="ns-support-reply-format__btn"
                        title={attachFileHint}
                        disabled={sending}
                        onClick={openReplyAttachmentPicker}
                      >
                        <BsPaperclip size={15} />
                      </button>
                    </div>
                    <div className="ns-support-reply-actions">
                      {can("support.tickets.manage") ? (
                        <button
                          type="button"
                          className="btn_theme_primary ns-support-reply-send"
                          onClick={() => {
                            setShowFormatToolbar(false);
                            setShowEmojiPicker(false);
                            onReply();
                          }}
                          disabled={sending || (isEmptyQuillHtml(reply) && !replyAttachmentItems.length)}
                        >
                          <BsSendFill size={14} aria-hidden />
                          <span>{sending ? "Sending…" : "Send"}</span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
                </>
              ) : null}
            </div>
          </main>
          </div>
        </div>
      </div>

      {profileDrawerEmail && activeRequesterProfile ? (
        <>
          <div
            className="ns-support-profile-drawer__backdrop"
            role="presentation"
            onClick={() => setProfileDrawerEmail(null)}
          />
          <div className="ns-support-profile-drawer" role="dialog" aria-label="Requester tickets">
            <div className="ns-support-profile-drawer__head">
              <div
                className="ns-support-msg__avatar"
                style={{
                  width: 44,
                  height: 44,
                  background: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
                }}
              >
                {activeRequesterProfile.initials}
              </div>
              <div className="flex-grow-1 min-w-0">
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {activeRequesterProfile.name || activeRequesterProfile.email}
                </div>
                {activeRequesterProfile.email ? (
                  <div className="text-muted small text-truncate">{activeRequesterProfile.email}</div>
                ) : null}
                <div className="text-muted small mt-1">Interaction history</div>
              </div>
              <button type="button" className="btn btn-sm btn-light" onClick={() => setProfileDrawerEmail(null)}>
                <BsX size={18} />
              </button>
            </div>
            <div className="ns-support-profile-drawer__list" ref={profileDrawerListRef}>
              {requesterTickets.length === 0 ? (
                <p className="text-muted small mb-0">No tickets found for this requester.</p>
              ) : (
                requesterTickets.map((conv) => {
                  const isCurrent = String(conv.id) === String(selected?.id);
                  return (
                    <div
                      key={conv.id}
                      data-requester-ticket-row={conv.id}
                      className={`ns-support-requester-ticket${isCurrent ? " ns-support-requester-ticket--current" : ""}`}
                      onMouseEnter={() => setHoveredRequesterTicketId(conv.id)}
                      onMouseLeave={() => setHoveredRequesterTicketId(null)}
                    >
                      <button
                        type="button"
                        className="ns-support-requester-ticket__row"
                        onClick={() => openRequesterTicket(conv)}
                      >
                        <span className={`ns-support-requester-ticket__status ${ticketStatusBadgeClass(conv.status)}`}>
                          {formatTicketStatus(conv.status)}
                        </span>
                        <span className="ns-support-requester-ticket__subject text-truncate">
                          {conv.subject || "(No subject)"}
                        </span>
                        <span className="ns-support-requester-ticket__date text-muted">
                          {conv.createdAt ? formatDateTime(conv.createdAt) : ""}
                        </span>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          {hoveredRequesterTicket && requesterPreviewPos
            ? createPortal(
                <div
                  className="ns-support-requester-ticket__preview ns-support-requester-ticket__preview--portal"
                  role="tooltip"
                  style={{
                    top: requesterPreviewPos.top,
                    left: requesterPreviewPos.left,
                    width: requesterPreviewPos.width,
                  }}
                >
                  {renderRequesterTicketPreviewCard(hoveredRequesterTicket)}
                </div>,
                document.body
              )
            : null}
        </>
      ) : null}

      <Modal
        show={!!attachmentPreview}
        onHide={() => setAttachmentPreview(null)}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            {attachmentPreview?.fileName || "Attachment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: 620 }}>
          {!attachmentPreview ? null : attachmentPreview.isImg ? (
            <div className="text-center">
              <img
                src={attachmentPreview.url}
                alt={attachmentPreview.fileName}
                style={{ maxWidth: "100%", maxHeight: 700, borderRadius: 10, border: "1px solid #e9ecef" }}
              />
            </div>
          ) : attachmentPreview.isPdf ? (
            <iframe
              title="Attachment preview"
              src={attachmentPreview.url}
              style={{ width: "100%", height: 700, border: "1px solid #e9ecef", borderRadius: 10 }}
            />
          ) : (
            <div className="p-3">
              <div className="mb-2" style={{ fontWeight: 700 }}>
                Preview not available for this file type.
              </div>
              <a className="btn btn-primary" href={attachmentPreview.url} target="_blank" rel="noreferrer">
                Open / Download
              </a>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {attachmentPreview?.url ? (
            <a className="btn btn-outline-secondary" href={attachmentPreview.url} target="_blank" rel="noreferrer">
              Open in new tab
            </a>
          ) : null}
          <button type="button" className="btn btn-secondary" onClick={() => setAttachmentPreview(null)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AppSupportTicketDetails;
